import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, CalendarOff, FolderTree, Pencil, Plus, Tags, Trash2, Users } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { EventNeedsTab } from '../components/event-needs-setup';
import { FacilitiesEmailTab, RoutingRulesTab } from '../components/facilities-setup';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Select,
  Switch,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { AUDIENCE_DESCRIPTION, AUDIENCE_LABEL, indentedName, useDepartments, WEEKDAYS } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { useDesk, type Desk } from '../lib/desk';
import { FACILITIES_ROLE_LABEL } from '../lib/facilities';
import { formatDate, PERSON_TYPE_LABEL, ROLE_LABEL } from '../lib/format';
import { useUrlState } from '../lib/hooks';
import { TICKET_FIELD_TYPE_LABEL } from '../lib/reports';
import { REQUEST_FORM_FIELDS, useRequestForm, type RequestFormField } from '../lib/ticket-images';
import type { Department, DepartmentAudience, HelpdeskSettings, PersonType, TeamMember, TicketField, TicketFieldType, WeeklyHours } from '../lib/types';

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

type DepartmentDraft = {
  id?: string;
  name: string;
  description: string;
  parentId: string;
  audience: DepartmentAudience;
  audiencePersonTypes: PersonType[];
  satisfactionSurvey: boolean;
};

/** Audience wording, with the facilities team named on facilities pages. */
const audienceLabel = (desk: Desk, audience: DepartmentAudience) =>
  desk.facilities ? AUDIENCE_LABEL[audience].replace('Technology team', 'Facilities team') : AUDIENCE_LABEL[audience];
const audienceDescription = (desk: Desk, audience: DepartmentAudience) =>
  desk.facilities ? AUDIENCE_DESCRIPTION[audience].replace('the technology team', desk.team) : AUDIENCE_DESCRIPTION[audience];
const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
/** The role that matters on this help desk: a facilities role for facilities, unless they're a school administrator. */
const roleLabel = (desk: Desk, person: TeamMember) =>
  desk.facilities && person.role !== 'ADMIN' && person.facilitiesRole ? FACILITIES_ROLE_LABEL[person.facilitiesRole] : ROLE_LABEL[person.role];

function DepartmentModal({ draft, departments, onClose }: { draft: DepartmentDraft | null; departments: Department[]; onClose(): void }) {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DepartmentDraft | null>(draft);
  const [error, setError] = useState('');
  useEffect(() => {
    setForm(draft);
    setError('');
  }, [draft]);

  const save = useMutation({
    mutationFn: ({ id, ...body }: DepartmentDraft) => {
      const payload = { ...body, parentId: body.parentId || null };
      return id ? api.patch(`/departments/${id}`, payload) : api.post(desk.path('/departments'), payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success(`${capitalize(desk.departmentWord)} saved`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  if (!form) return null;
  // A department can't move under itself or anything below it.
  const parents = departments.filter((d) => !form.id || (d.id !== form.id && !d.ancestorIds.includes(form.id)));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(form);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? `Edit ${form.name}` : `Add a ${desk.departmentWord}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="department-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="department-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Name" required>
          <Input value={form.name} maxLength={120} required autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={desk.facilities ? 'e.g. Plumbing' : 'e.g. Wireless'} />
        </Field>
        <Field label="Inside">
          <Select value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
            <option value="">Top level</option>
            {parents.map((department) => (
              <option key={department.id} value={department.id}>
                {indentedName(department)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Description">
          <Textarea rows={2} maxLength={1000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={desk.facilities ? 'What kinds of requests go here' : 'What kinds of tickets go here'} />
        </Field>
        <Field label="Who can choose it" hint={audienceDescription(desk, form.audience)}>
          <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value as DepartmentAudience })}>
            {(Object.keys(AUDIENCE_LABEL) as DepartmentAudience[]).map((audience) => (
              <option key={audience} value={audience}>
                {audienceLabel(desk, audience)}
              </option>
            ))}
          </Select>
        </Field>
        {form.audience === 'CUSTOM' && (
          <div className="grid grid-cols-2 gap-1.5">
            {(Object.keys(PERSON_TYPE_LABEL) as PersonType[]).map((type) => (
              <label key={type} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.audiencePersonTypes.includes(type)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      audiencePersonTypes: e.target.checked ? [...form.audiencePersonTypes, type] : form.audiencePersonTypes.filter((t) => t !== type),
                    })
                  }
                  className="size-4 rounded border-slate-300"
                />
                {PERSON_TYPE_LABEL[type]}
              </label>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500">
          Requesters only see a {desk.departmentWord} if they can choose it and every {desk.departmentWord} above it.
        </p>
        <Switch
          checked={form.satisfactionSurvey}
          onChange={(satisfactionSurvey) => setForm({ ...form, satisfactionSurvey })}
          label="Ask requesters how it went"
          description="When a ticket here or in a sub-department is resolved, the requester is asked to rate it. Results appear in Reports."
        />
      </form>
    </Modal>
  );
}

function MembersModal({ department, onClose }: { department: Department | null; onClose(): void }) {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const team = useQuery({
    queryKey: ['helpdesk-team', desk.service],
    queryFn: () => api.get<TeamMember[]>('/helpdesk/team', desk.query()),
    enabled: !!department,
  });
  const [members, setMembers] = useState<Record<string, { isLead: boolean; roundRobin: boolean }>>({});

  useEffect(() => {
    if (department) setMembers(Object.fromEntries(department.members.map((m) => [m.userId, { isLead: m.isLead, roundRobin: m.roundRobin }])));
  }, [department]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/departments/${department!.id}/members`, {
        members: Object.entries(members).map(([userId, flags]) => ({ userId, ...flags })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
      void queryClient.invalidateQueries({ queryKey: ['helpdesk-team'] });
      toast.success('Members saved');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!department) return null;
  const set = (userId: string, patch: Partial<{ isLead: boolean; roundRobin: boolean }> | null) =>
    setMembers((current) => {
      const next = { ...current };
      if (patch === null) delete next[userId];
      else next[userId] = { ...(current[userId] ?? { isLead: false, roundRobin: true }), ...patch };
      return next;
    });

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={desk.facilities ? `${department.path} crew` : `${department.path} members`}
      description={
        desk.facilities
          ? 'The crew takes the requests routing rules send to this category. The lead gets requests sent to the crew’s lead.'
          : 'Members take this department’s round-robin tickets. Leads get escalations.'
      }
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      {team.isLoading ? (
        <PageLoader />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Member</Th>
              <Th>Takes round-robin tickets</Th>
              <Th>Lead</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {team.data?.map((person) => {
              const member = members[person.userId];
              return (
                <tr key={person.userId}>
                  <Td>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!member} onChange={(e) => set(person.userId, e.target.checked ? {} : null)} className="size-4 rounded border-slate-300" />
                      <span>
                        <span className="font-medium text-slate-900">{person.name}</span>
                        <span className="block text-xs text-slate-500">
                          {roleLabel(desk, person)}
                          {person.away && ' · away'}
                        </span>
                      </span>
                    </label>
                  </Td>
                  <Td>
                    <input type="checkbox" disabled={!member} checked={!!member?.roundRobin} onChange={(e) => set(person.userId, { roundRobin: e.target.checked })} className="size-4 rounded border-slate-300" aria-label="Round-robin" />
                  </Td>
                  <Td>
                    <input type="checkbox" disabled={!member} checked={!!member?.isLead} onChange={(e) => set(person.userId, { isLead: e.target.checked })} className="size-4 rounded border-slate-300" aria-label="Lead" />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Modal>
  );
}

function DepartmentsTab() {
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const departments = useDepartments();
  const [editing, setEditing] = useState<DepartmentDraft | null>(null);
  const [membersOf, setMembersOf] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/departments/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
      setDeleting(null);
      toast.success(`${capitalize(desk.departmentWord)} deleted`);
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const blank = (parentId = ''): DepartmentDraft => ({ name: '', description: '', parentId, audience: 'EVERYONE', audiencePersonTypes: [], satisfactionSurvey: false });

  return (
    <Card>
      <CardHeader
        title={desk.facilities ? 'Categories and crews' : 'Departments'}
        description={
          desk.facilities
            ? 'What people can ask for, grouped under the crews that handle it (Maintenance › Plumbing). Put people on a category’s crew, and routing rules send requests to them.'
            : 'Nest departments as deep as you need (IT › Networking › Wireless). Workflows route tickets into them.'
        }
        actions={
          canEdit && (
            <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing(blank())}>
              Add {desk.departmentWord}
            </Button>
          )
        }
      />
      {departments.isLoading ? (
        <PageLoader />
      ) : departments.error ? (
        <ErrorState error={departments.error} onRetry={() => departments.refetch()} />
      ) : departments.data!.length === 0 ? (
        <EmptyState
          icon={<FolderTree />}
          title={`No ${desk.departmentsLabel.toLowerCase()} yet`}
          description={desk.facilities ? 'Add the kinds of work people can request, like Plumbing or Grounds.' : 'Add one here, or add the examples from the Automation page.'}
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {departments.data!.map((department) => {
            const lead = department.members.find((m) => m.isLead);
            const rotation = department.members.filter((m) => m.roundRobin).length;
            return (
              <li key={department.id} className="flex flex-wrap items-center gap-3 py-3 pr-5" style={{ paddingLeft: `${1.25 + department.depth * 1.75}rem` }}>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                    {department.name}
                    <Badge tone={department.audience === 'ADMIN_ONLY' ? 'violet' : department.audience === 'EVERYONE' ? 'gray' : 'blue'}>
                      {department.audience === 'CUSTOM'
                        ? department.audiencePersonTypes.map((t) => PERSON_TYPE_LABEL[t]).join(', ')
                        : audienceLabel(desk, department.audience)}
                    </Badge>
                    {department.satisfactionSurvey && <Badge tone="green">Surveys</Badge>}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {[
                      department.members.length
                        ? `${department.members.length} ${desk.facilities ? 'on the crew' : 'members'}, ${rotation} in rotation`
                        : desk.facilities
                          ? 'No crew'
                          : 'No members',
                      lead && `lead: ${lead.name}`,
                      department.description,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <Link to={`${desk.base}/work-orders?departmentId=${department.id}`} className="text-xs text-brand-700 hover:underline">
                  {department.openTickets} open
                </Link>
                {canEdit && (
                  <div className="flex whitespace-nowrap">
                    <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setEditing(blank(department.id))}>
                      {desk.facilities ? 'Sub-category' : 'Sub-department'}
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Users />} onClick={() => setMembersOf(department)}>
                      {desk.facilities ? 'Crew' : 'Members'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Pencil />}
                      onClick={() =>
                        setEditing({
                          id: department.id,
                          name: department.name,
                          description: department.description ?? '',
                          parentId: department.parentId ?? '',
                          audience: department.audience,
                          audiencePersonTypes: department.audiencePersonTypes,
                          satisfactionSurvey: department.satisfactionSurvey,
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(department)} aria-label={`Delete ${department.name}`} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <DepartmentModal draft={editing} departments={departments.data ?? []} onClose={() => setEditing(null)} />
      <MembersModal department={membersOf} onClose={() => setMembersOf(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={`Delete ${deleting?.name}?`}
        description={
          desk.facilities
            ? 'Its work orders stay, without a category. Categories that still have sub-categories can’t be deleted.'
            : 'Its tickets stay, without a department. Departments that still have sub-departments, or that a workflow uses, can’t be deleted.'
        }
        confirmLabel={`Delete ${desk.departmentWord}`}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ticket fields
// ---------------------------------------------------------------------------

type FieldDraft = { id?: string; name: string; description: string; type: TicketFieldType; options: string; departmentIds: string[]; ticketCount?: number };

function TicketFieldModal({ draft, departments, onClose }: { draft: FieldDraft | null; departments: Department[]; onClose(): void }) {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FieldDraft | null>(draft);
  const [error, setError] = useState('');
  useEffect(() => {
    setForm(draft);
    setError('');
  }, [draft]);

  const save = useMutation({
    mutationFn: ({ id, ...body }: FieldDraft) => {
      const payload = {
        name: body.name,
        description: body.description.trim() || null,
        type: body.type,
        options: body.type === 'SELECT' ? body.options.split('\n').map((option) => option.trim()).filter(Boolean) : [],
        departmentIds: body.departmentIds,
      };
      return id ? api.patch(`/ticket-fields/${id}`, payload) : api.post(desk.path('/ticket-fields'), payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ticket-fields'] });
      toast.success('Ticket field saved');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  if (!form) return null;
  const typeLocked = !!form.id && (form.ticketCount ?? 0) > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(form);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? `Edit ${form.name}` : 'Add a ticket field'}
      description="Technicians fill this in on tickets, and reports can group ticket volume by it."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="ticket-field-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="ticket-field-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Name" required>
          <Input value={form.name} maxLength={80} required autoFocus onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={desk.facilities ? 'e.g. Repair type' : 'e.g. Damage type'} />
        </Field>
        <Field label="Kind of answer" hint={typeLocked ? 'Tickets already use this field, so this can’t change.' : undefined}>
          <Select value={form.type} disabled={typeLocked} onChange={(e) => setForm({ ...form, type: e.target.value as TicketFieldType })}>
            {(Object.keys(TICKET_FIELD_TYPE_LABEL) as TicketFieldType[]).map((type) => (
              <option key={type} value={type}>
                {TICKET_FIELD_TYPE_LABEL[type]}
              </option>
            ))}
          </Select>
        </Field>
        {form.type === 'SELECT' && (
          <Field label="Choices" hint="One per line" required>
            <Textarea rows={5} value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder={desk.facilities ? 'Patched\nReplaced a part\nCalled a contractor' : 'Cracked screen\nLiquid damage\nMissing keys'} />
          </Field>
        )}
        <Field label="Help text" hint="Shown under the field on tickets">
          <Input value={form.description} maxLength={300} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        {departments.length > 0 && (
          <div>
            <p className="text-sm font-medium text-slate-700">Show on tickets in</p>
            <p className="text-xs text-slate-500">Leave these unchecked to show it on every ticket. Sub-{desk.facilities ? 'categories' : 'departments'} are included.</p>
            <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {departments.map((department) => (
                <label key={department.id} className="flex items-center gap-2 text-sm text-slate-700" style={{ paddingLeft: `${department.depth * 1.25}rem` }}>
                  <input
                    type="checkbox"
                    checked={form.departmentIds.includes(department.id)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        departmentIds: e.target.checked ? [...form.departmentIds, department.id] : form.departmentIds.filter((id) => id !== department.id),
                      })
                    }
                    className="size-4 rounded border-slate-300"
                  />
                  {department.name}
                </label>
              ))}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

function TicketFieldsTab() {
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const fields = useQuery({
    queryKey: ['ticket-fields', desk.service, 'all'],
    queryFn: () => api.get<TicketField[]>('/ticket-fields', desk.query({ includeArchived: true })),
  });
  const departments = useDepartments();
  const [editing, setEditing] = useState<FieldDraft | null>(null);
  const [deleting, setDeleting] = useState<TicketField | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ticket-fields'] });
  const archive = useMutation({
    mutationFn: (field: TicketField) => api.patch(`/ticket-fields/${field.id}`, { archived: !field.archived }),
    onSuccess: (_, field) => {
      void refresh();
      toast.success(field.archived ? `${field.name} restored` : `${field.name} archived`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/ticket-fields/${id}`),
    onSuccess: () => {
      void refresh();
      setDeleting(null);
      toast.success('Ticket field deleted');
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const pathOf = (id: string) => departments.data?.find((d) => d.id === id)?.path ?? `a deleted ${desk.departmentWord}`;

  return (
    <Card>
      <CardHeader
        title="Ticket fields"
        description="Fields technicians fill in on tickets, such as damage type. Reports group ticket volume by them."
        actions={
          canEdit && (
            <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing({ name: '', description: '', type: 'SELECT', options: '', departmentIds: [] })}>
              Add field
            </Button>
          )
        }
      />
      {fields.isLoading ? (
        <PageLoader />
      ) : fields.error ? (
        <ErrorState error={fields.error} onRetry={() => fields.refetch()} />
      ) : fields.data!.length === 0 ? (
        <EmptyState icon={<Tags />} title="No ticket fields yet" description="Add one for anything you want to count later, such as the kind of damage or the repair vendor." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {fields.data!.map((field) => (
            <li key={field.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                  {field.name}
                  <Badge>{TICKET_FIELD_TYPE_LABEL[field.type]}</Badge>
                  {field.archived && <Badge tone="amber">Archived</Badge>}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {[
                    field.type === 'SELECT' && field.options.join(', '),
                    field.departmentIds.length ? `In ${field.departmentIds.map(pathOf).join(', ')}` : 'On every ticket',
                    `filled in on ${field.ticketCount ?? 0} ticket${field.ticketCount === 1 ? '' : 's'}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              {canEdit && (
                <div className="flex whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Pencil />}
                    onClick={() =>
                      setEditing({
                        id: field.id,
                        name: field.name,
                        description: field.description ?? '',
                        type: field.type,
                        options: field.options.join('\n'),
                        departmentIds: field.departmentIds,
                        ticketCount: field.ticketCount,
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={field.archived ? <ArchiveRestore /> : <Archive />}
                    loading={archive.isPending && archive.variables?.id === field.id}
                    onClick={() => archive.mutate(field)}
                  >
                    {field.archived ? 'Restore' : 'Archive'}
                  </Button>
                  {!field.ticketCount && <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(field)} aria-label={`Delete ${field.name}`} />}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <TicketFieldModal draft={editing} departments={departments.data ?? []} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={`Delete ${deleting?.name}?`}
        description="No tickets use it yet. Fields that tickets use can be archived instead, so reports keep their history."
        confirmLabel="Delete field"
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Business hours & holidays
// ---------------------------------------------------------------------------

function BusinessHoursTab() {
  const { can } = useAuth();
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  // Closures are shared by both help desks, so school administrators manage them.
  const canEditClosures = can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['helpdesk-settings', desk.service], queryFn: () => api.get<HelpdeskSettings>('/helpdesk/settings', desk.query()) });
  const [hours, setHours] = useState<WeeklyHours | null>(null);
  const [holiday, setHoliday] = useState({ name: '', startDate: '', endDate: '' });

  useEffect(() => {
    if (settings.data) setHours(settings.data.businessHours);
  }, [settings.data]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['helpdesk-settings'] });
  const saveHours = useMutation({
    mutationFn: () => api.put(desk.path('/helpdesk/business-hours'), { businessHours: hours }),
    onSuccess: () => {
      void refresh();
      toast.success('Business hours saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const addHoliday = useMutation({
    mutationFn: () => api.post('/helpdesk/holidays', { ...holiday, endDate: holiday.endDate || holiday.startDate }),
    onSuccess: () => {
      void refresh();
      setHoliday({ name: '', startDate: '', endDate: '' });
      toast.success('Closure added');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const removeHoliday = useMutation({
    mutationFn: (id: string) => api.delete(`/helpdesk/holidays/${id}`),
    onSuccess: () => void refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (settings.isLoading || !hours) return <PageLoader />;
  if (settings.error) return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  const data = settings.data!;
  const setDay = (day: number, range: [string, string] | null) => setHours({ ...hours, [String(day)]: range ? [range] : [] });
  const dateLabel = (iso: string) => formatDate(new Date(`${iso}T12:00:00`));

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader
          title="Business hours"
          description={`Timers set to “business hours” only count this time. Times are in ${data.timezone}.`}
          actions={
            canEdit && (
              <Button size="sm" variant="primary" loading={saveHours.isPending} onClick={() => saveHours.mutate()}>
                Save
              </Button>
            )
          }
        />
        <ul className="divide-y divide-slate-100">
          {[1, 2, 3, 4, 5, 6, 0].map((day) => {
            const range = hours[String(day)]?.[0] ?? null;
            return (
              <li key={day} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="w-36">
                  <Switch checked={!!range} disabled={!canEdit} onChange={(open) => setDay(day, open ? ['07:30', '16:00'] : null)} label={WEEKDAYS[day]} />
                </div>
                {range ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Input type="time" disabled={!canEdit} value={range[0]} onChange={(e) => setDay(day, [e.target.value, range[1]])} className="w-32" aria-label={`${WEEKDAYS[day]} opens`} />
                    <span>to</span>
                    <Input type="time" disabled={!canEdit} value={range[1]} onChange={(e) => setDay(day, [range[0], e.target.value])} className="w-32" aria-label={`${WEEKDAYS[day]} closes`} />
                  </div>
                ) : (
                  <span className="text-sm text-slate-400">Closed</span>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      <Card>
        <CardHeader
          title="Holidays & closures"
          description={
            desk.facilities
              ? 'Shared with the technology help desk, so school administrators add and remove them.'
              : 'Business-hours timers pause and schedules can skip these days.'
          }
        />
        {canEditClosures && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addHoliday.mutate();
            }}
            className="grid gap-3 border-b border-slate-100 p-5 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end"
          >
            <Field label="Name">
              <Input value={holiday.name} required maxLength={120} onChange={(e) => setHoliday({ ...holiday, name: e.target.value })} placeholder="e.g. Winter break" />
            </Field>
            <Field label="From">
              <Input type="date" required value={holiday.startDate} onChange={(e) => setHoliday({ ...holiday, startDate: e.target.value })} />
            </Field>
            <Field label="Through">
              <Input type="date" value={holiday.endDate} min={holiday.startDate} onChange={(e) => setHoliday({ ...holiday, endDate: e.target.value })} />
            </Field>
            <Button type="submit" icon={<Plus />} loading={addHoliday.isPending}>
              Add
            </Button>
          </form>
        )}
        {data.holidays.length === 0 ? (
          <EmptyState icon={<CalendarOff />} title="No closures yet" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.holidays.map((h) => (
              <li key={h.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                <span className="min-w-0 flex-1 font-medium text-slate-900">{h.name}</span>
                <span className="text-slate-500">{h.startDate === h.endDate ? dateLabel(h.startDate) : `${dateLabel(h.startDate)} – ${dateLabel(h.endDate)}`}</span>
                {canEditClosures && (
                  <button type="button" onClick={() => removeHoliday.mutate(h.id)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600" aria-label={`Remove ${h.name}`}>
                    <Trash2 className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team availability
// ---------------------------------------------------------------------------

const toDateInput = (value: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const localMidnight = (value: string) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).toISOString();
};

function AwayModal({ member, onClose }: { member: TeamMember | null; onClose(): void }) {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ from: '', back: '', note: '' });
  useEffect(() => {
    if (member) setForm({ from: toDateInput(member.awayFrom), back: toDateInput(member.awayUntil), note: member.awayNote ?? '' });
  }, [member]);

  const save = useMutation({
    mutationFn: () =>
      api.put(desk.path(`/helpdesk/team/${member!.userId}/availability`), {
        awayFrom: form.from ? localMidnight(form.from) : null,
        awayUntil: localMidnight(form.back),
        awayNote: form.note,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['helpdesk-team'] });
      toast.success('Availability saved');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!member) return null;
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`${member.name} is away`}
      description="Round-robin skips them while they’re away. Tickets already assigned stay with them."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!form.back} loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First day away" hint="Leave empty to start now">
            <Input type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
          </Field>
          <Field label="Back on" required>
            <Input type="date" value={form.back} min={form.from || undefined} onChange={(e) => setForm({ ...form, back: e.target.value })} />
          </Field>
        </div>
        <Field label="Note">
          <Input value={form.note} maxLength={200} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Vacation" />
        </Field>
      </div>
    </Modal>
  );
}

function TeamTab() {
  const { me } = useAuth();
  const desk = useDesk();
  const can = desk.can;
  const toast = useToast();
  const queryClient = useQueryClient();
  const team = useQuery({ queryKey: ['helpdesk-team', desk.service], queryFn: () => api.get<TeamMember[]>('/helpdesk/team', desk.query()) });
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const clear = useMutation({
    mutationFn: (userId: string) => api.put(desk.path(`/helpdesk/team/${userId}/availability`), { awayUntil: null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['helpdesk-team'] });
      toast.success('Marked available');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (team.isLoading) return <PageLoader />;
  if (team.error) return <ErrorState error={team.error} onRetry={() => team.refetch()} />;

  return (
    <Card>
      <CardHeader
        title="Team availability"
        description={desk.facilities ? 'Mark vacations and leave so routing skips people who are out.' : 'Mark vacations and leave so round-robin assignment skips people who are out.'}
      />
      <Table>
        <thead>
          <tr>
            <Th>Person</Th>
            <Th>{desk.facilities ? 'Crews' : 'Departments'}</Th>
            <Th>Availability</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {team.data!.map((member) => {
            const scheduled = !member.away && !!member.awayUntil && new Date(member.awayUntil) > new Date();
            const editable = can('ADMIN') || member.userId === me?.user.id;
            return (
              <tr key={member.userId}>
                <Td>
                  <p className="font-medium text-slate-900">
                    {member.name}
                    {member.userId === me?.user.id && <span className="ml-1.5 text-xs font-normal text-slate-400">(you)</span>}
                  </p>
                  <p className="text-xs text-slate-500">{roleLabel(desk, member)}</p>
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {member.departments.length === 0 && <span className="text-xs text-slate-400">None</span>}
                    {member.departments.map((d) => (
                      <Badge key={d.departmentId} tone={d.isLead ? 'brand' : 'gray'}>
                        {d.path}
                        {d.isLead && ' · lead'}
                      </Badge>
                    ))}
                  </div>
                </Td>
                <Td>
                  {member.away ? (
                    <Badge tone="amber">Away until {formatDate(member.awayUntil)}</Badge>
                  ) : scheduled ? (
                    <Badge tone="blue">
                      Away {formatDate(member.awayFrom)} – {formatDate(member.awayUntil)}
                    </Badge>
                  ) : (
                    <Badge tone="green">Available</Badge>
                  )}
                  {member.awayNote && (member.away || scheduled) && <p className="mt-0.5 text-xs text-slate-500">{member.awayNote}</p>}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {editable && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(member)}>
                        {member.away || scheduled ? 'Change' : 'Set away'}
                      </Button>
                      {(member.away || scheduled) && (
                        <Button size="sm" variant="ghost" loading={clear.isPending && clear.variables === member.userId} onClick={() => clear.mutate(member.userId)}>
                          Mark available
                        </Button>
                      )}
                    </>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <AwayModal member={editing} onClose={() => setEditing(null)} />
    </Card>
  );
}

/** The technology help desk's "Report a problem" form: which optional fields requesters see. */
function RequestFormTab() {
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useRequestForm();

  const save = useMutation({
    mutationFn: (hiddenFields: RequestFormField[]) => api.put<{ hiddenFields: RequestFormField[] }>('/helpdesk/request-form', { hiddenFields }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['request-form'], saved);
      toast.success('Report a problem form saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (form.error) return <ErrorState error={form.error} onRetry={() => form.refetch()} />;
  if (!form.data) return <PageLoader />;
  const hidden = form.data.hiddenFields;

  return (
    <Card>
      <CardHeader
        title="Report a problem form"
        description="Choose the optional fields teachers, staff, and students see when they report a problem. Technicians see every field when they log a work order."
      />
      <div className="max-w-xl space-y-4 p-5">
        {REQUEST_FORM_FIELDS.map((field) => {
          const shown = !hidden.includes(field.value);
          return (
            <Switch
              key={field.value}
              checked={shown}
              disabled={!canEdit || save.isPending}
              onChange={(show) => save.mutate(show ? hidden.filter((value) => value !== field.value) : [...hidden, field.value])}
              label={`“${field.label}”`}
              description={shown ? (field.value === 'DEPARTMENT' ? 'Shown when there are departments people can choose.' : 'Shown on the form.') : `Hidden. ${field.hidden}`}
            />
          );
        })}
        <p className="border-t border-slate-100 pt-4 text-xs text-slate-500">
          {canEdit ? 'Screenshots and photos are always optional on the form.' : 'Only administrators can change the form.'}
        </p>
      </div>
    </Card>
  );
}

type Tab = 'departments' | 'routing' | 'fields' | 'request-form' | 'hours' | 'team' | 'events' | 'email';

/** Help desk setup. Under /facilities, the same for facilities, plus routing rules and the facilities mailbox. */
export function HelpdeskSetupPage() {
  const desk = useDesk();
  const [state, setState] = useUrlState({ tab: 'departments' });
  const tabs: { value: Tab; label: string }[] = desk.facilities
    ? [
        { value: 'departments', label: 'Categories & crews' },
        { value: 'routing', label: 'Routing rules' },
        { value: 'fields', label: 'Work order fields' },
        { value: 'hours', label: 'Hours & closures' },
        { value: 'team', label: 'Team availability' },
        { value: 'events', label: 'Event requests' },
        ...(desk.can('ADMIN') ? [{ value: 'email' as const, label: 'Email' }] : []),
      ]
    : [
        { value: 'departments', label: 'Departments' },
        { value: 'fields', label: 'Ticket fields' },
        { value: 'request-form', label: 'Report a problem form' },
        { value: 'hours', label: 'Business hours & holidays' },
        { value: 'team', label: 'Team availability' },
        { value: 'events', label: 'Event requests' },
      ];
  const tab = (tabs.some((t) => t.value === state.tab) ? state.tab : 'departments') as Tab;
  return (
    <>
      <PageHeader
        title={desk.facilities ? 'Facilities setup' : 'Help desk setup'}
        description={
          desk.facilities
            ? 'Request categories and crews, where requests go, work order fields, hours, who’s available, maintenance needs for events, and the facilities mailbox.'
            : 'Departments, ticket fields, the Report a problem form, business hours, holidays, who’s available to take tickets, and tech needs for events.'
        }
      />
      <Tabs<Tab> className={cn('mb-6')} value={tab} onChange={(value) => setState({ tab: value })} tabs={tabs} />
      {tab === 'departments' && <DepartmentsTab />}
      {tab === 'routing' && <RoutingRulesTab />}
      {tab === 'fields' && <TicketFieldsTab />}
      {tab === 'request-form' && <RequestFormTab />}
      {tab === 'hours' && <BusinessHoursTab />}
      {tab === 'team' && <TeamTab />}
      {tab === 'events' && <EventNeedsTab />}
      {tab === 'email' && <FacilitiesEmailTab />}
    </>
  );
}
